import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MaxOSCloudPreview from "./MaxOSCloudPreview";
import SupportChatPanel from "../components/SupportChatPanel";
import OnboardingTour from "../components/OnboardingTour";
import DashboardTour from "../components/DashboardTour";
import OnboardingWizard from "../components/OnboardingWizard";
import InteractiveTutorial from "../components/InteractiveTutorial";
import ReferralWidget from "../components/ReferralWidget";
import ConnectedIntegrationsWidget from "../components/ConnectedIntegrationsWidget";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  ShoppingBag, ArrowRight, Package, GraduationCap, Repeat, XCircle,
  Cpu, HardDrive, Plug, ShieldCheck, Zap, Plus, CreditCard,
  Activity, Clock, CheckCircle2, AlertTriangle, XCircle as XCircleIcon,
  Bot, Users, BarChart3, Settings, ExternalLink, ChevronRight,
  Sparkles, Lock, Globe, MessageSquare, Mail, Phone, Database,
  Cloud, Server, Eye, Bell, Star, TrendingUp, CircleDot, Download,
  FlaskConical, Target, Workflow,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { messagesLast7Days } from "../data/analyticsData";
import { getEnrichedTests } from "../data/abTests";
import { loadAutomations } from "../data/automations";
import SEOHead from "../components/SEOHead";

function UsageBar({ pct, label, sublabel }) {
  const color =
    pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-400" : "bg-emerald-500";
  const textColor =
    pct >= 90 ? "text-red-400" : pct >= 75 ? "text-amber-400" : "text-emerald-400";
  const borderColor =
    pct >= 90 ? "border-red-500/30" : pct >= 75 ? "border-amber-400/30" : "border-emerald-500/30";
  return (
    <div className={`border ${borderColor} bg-zinc-950/50`}>
      <SEOHead
        title={t('dashboard.dashboard_max_employee')}
        description={t('dashboard.max_employee_dashboard_manage_your_ai_agents')}
        url="/dashboard"
        noIndex
      />
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          {label && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>}
          {sublabel && <span className="font-mono text-[10px] text-zinc-600">({sublabel})</span>}
        </div>
        <span className={`font-mono text-xs font-bold ${textColor}`}>{pct}%</span>
      </div>
      <div className="h-2 bg-zinc-900 w-full">
        <div
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
      {children}
    </div>
  );
}

function DashboardCard({ children, className = "" }) {
  return (
    <div className={`border border-zinc-800 bg-zinc-950/30 ${className}`}>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [courseAccess, setCourseAccess] = useState(false);
  const [supportAccess, setSupportAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [automations, setAutomations] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [ordersRes, subsRes, courseAccessRes, supportAccessRes, overviewRes, agentsRes, auditRes] = await Promise.all([
        api.get("/orders"),
        api.get("/subscriptions"),
        api.get("/course/access"),
        api.get("/support/access").catch(() => ({ data: { has_access: false } })),
        api.get("/dashboard/overview").catch(() => null),
        api.get("/dashboard/agents").catch(() => []),
        api.get("/dashboard/audit-events").catch(() => []),
      ]);
      setOrders(ordersRes.data);
      setSubs(subsRes.data);
      setCourseAccess(courseAccessRes.data.has_access);
      setSupportAccess(Boolean(supportAccessRes?.data?.has_access));
      if (overviewRes) setOverview(overviewRes.data);
      if (agentsRes) setAgents(Array.isArray(agentsRes.data) ? agentsRes.data : []);
      if (auditRes) setAuditEvents(Array.isArray(auditRes.data) ? auditRes.data : []);
      setAbTests(getEnrichedTests());
      setAutomations(loadAutomations());
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleDownload = async (productId) => {
    try {
      await api.get(`/downloads/${productId}`);
      window.open(`${process.env.REACT_APP_BACKEND_URL || ""}/api/downloads/file/${productId}`, "_blank");
      toast.success(t("dashboard.download_ready"));
    } catch {
      toast.error(t("dashboard.download_error"));
    }
  };

  const handleCancelSub = async (sessionId) => {
    try {
      await api.post(`/subscriptions/${sessionId}/cancel`);
      toast.success(t("dashboard.sub_cancel_success"));
      reload();
    } catch {
      toast.error(t("dashboard.sub_cancel_fail"));
    }
  };

  const totalSpent = orders.reduce((acc, o) => acc + (o.amount || 0), 0);
  const activeSubs = subs.filter(s => s.status === "active" && !s.cancel_at_period_end);
  const hasSupportAccess = user?.role === "admin" || supportAccess || orders.length > 0 || activeSubs.some(s => ["active", "expired"].includes(String(s.status || "").toLowerCase()));

  const activeCloudSub = subs.find(
    s => s.status === "active" && !s.cancel_at_period_end &&
    (s.product?.product_id === "max-os-cloud" || s.product?.product_id === "max-os-cloud-enterprise")
  );
  const currentPlan = activeCloudSub?.product?.name || "Max OS Cloud";
  const currentPlanPrice = activeCloudSub?.product?.price ? `$${Number(activeCloudSub.product.price).toFixed(0)}` : "$297";
  const currentPlanBilling = activeCloudSub?.product?.billing || "per month";

  const creditPct = overview?.token_usage_pct ?? 25.7;
  const creditUsed = overview?.token_used ?? 128750;
  const creditQuota = overview?.token_quota ?? 500000;

  const rolling5hUsed = overview?.rolling_5h_used ?? 23400;
  const rolling5hQuota = overview?.rolling_5h_quota ?? 100000;
  const rolling5hPct = overview?.rolling_5h_pct ?? 23.4;

  const storageUsed = overview?.storage_used_gb ?? 2.4;
  const storageQuota = overview?.storage_quota_gb ?? 10.0;
  const storagePct = Math.round((storageUsed / storageQuota) * 100);
  const activeIntegrations = overview?.active_integrations ?? 3;
  const agentCount = overview?.agent_count ?? 1;
  const maxAgents = overview?.max_agents ?? 2;
  const showPreviewForInactive = !loading && user?.role !== "admin" && !activeCloudSub;

  const integrationsList = [
    { name: "Telegram", icon: MessageSquare, status: "connected" },
    { name: "Email", icon: Mail, status: "connected" },
    { name: "WhatsApp", icon: Phone, status: "connected" },
    { name: "Discord", icon: MessageSquare, status: "available" },
    { name: "Slack", icon: MessageSquare, status: "available" },
    { name: "Google Workspace", icon: Globe, status: "available" },
  ];

  const formatCredits = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  };

  const auditIcon = (action) => {
    if (action.includes("warning") || action.includes("error")) return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
    if (action.includes("deployed") || action.includes("connected") || action.includes("renewed")) return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
    return <Activity className="w-3.5 h-3.5 text-zinc-500" />;
  };

  if (showPreviewForInactive) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />
        <section className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">// DASHBOARD</div>
            <h1 className="font-display text-3xl sm:text-5xl md:text-6xl uppercase leading-[0.95]">
              Welcome,<br />
              <span className="text-amber-500">{(user?.name || user?.email?.split("@")[0] || "CLIENT").toUpperCase()}</span>
            </h1>
          </div>
        </section>
        <div className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 border-x border-zinc-900">
            {[
              { l: t("dashboard.orders"), v: String(orders.length || 0) },
              { l: t("dashboard.subscriptions"), v: String(subs.length || 0) },
              { l: t("dashboard.total_spent"), v: `$${totalSpent || 0}` },
              { l: t("dashboard.course"), v: courseAccess ? t("dashboard.active") : t("dashboard.locked") },
            ].map((s) => (
              <div key={s.l} className="border-r last:border-r-0 border-zinc-900 p-5 md:p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">{s.l}</div>
                <div className={`font-display text-3xl md:text-4xl ${s.l === t("dashboard.course") && !courseAccess ? "text-white" : "text-white"}`}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10">
          <section>
            <div className="flex items-center justify-between mb-6">
              <SectionLabel>// LIBRARY</SectionLabel>
              <Link
                to="/pricing"
                data-testid="dashboard-shop-link"
                className="hidden sm:inline-flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
              >
                {t("dashboard.browse_catalog")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <h2 className="font-display text-2xl sm:text-4xl md:text-5xl uppercase mb-6">{t("dashboard.your_purchases")}</h2>
            <div data-testid="empty-orders" className="border border-zinc-900 p-12 text-center">
              <Package className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
              <div className="font-display text-xl uppercase mb-2">{t("dashboard.no_purchases")}</div>
              <p className="text-zinc-500 max-w-md mx-auto mb-6 text-sm">
                {t("dashboard.empty_library")}
              </p>
              <Link
                to="/pricing"
                data-testid="empty-shop-cta"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors"
              >
                {t("dashboard.browse_catalog")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </section>
        </div>
        <div className="border-t border-zinc-900 mt-16 pt-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <MaxOSCloudPreview embedded />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <OnboardingTour />
      <DashboardTour />
      <OnboardingWizard />
      <InteractiveTutorial />

      {/* Top bar */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">{t("dashboard.control_center")}</div>
              <h1 className="font-display text-3xl md:text-4xl uppercase">
                {t("dashboard.welcome_back")}<br />
                <span className="text-amber-500">{user?.name || user?.email?.split("@")[0]}</span>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 border border-zinc-800 bg-zinc-950/50">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t("dashboard.system_active")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick stats row */}
      <div className="border-b border-zinc-900" data-tour="stats-row">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 border-x border-zinc-900">
          {[
            { l: t("dashboard.plan"), v: currentPlan, i: Zap, accent: "text-amber-500" },
            { l: t("dashboard.agents"), v: `${agentCount}/${maxAgents}`, i: Bot, accent: "text-sky-400" },
            { l: t("dashboard.credits"), v: `${formatCredits(creditUsed)}/${formatCredits(creditQuota)}`, i: BarChart3, accent: creditPct >= 90 ? "text-red-400" : creditPct >= 75 ? "text-amber-400" : "text-emerald-400" },
            { l: t("dashboard.status"), v: t("dashboard.active"), i: ShieldCheck, accent: "text-emerald-500" },
          ].map((s) => {
            const I = s.i;
            return (
              <div key={s.l} className="border-r last:border-r-0 border-zinc-900 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.l}</div>
                  <I className={`w-4 h-4 ${s.accent}`} />
                </div>
                <div className="font-display text-xl md:text-2xl truncate">{s.v}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10 space-y-6 sm:space-y-10 safe-area-bottom">

        <section data-tour="support-section">
        <section data-tour="first-contact">
          <div className="flex items-center justify-between gap-4 mb-4">
            <SectionLabel>{t("dashboard.first_contact")}</SectionLabel>
            {hasSupportAccess && (
              <Link
                to="/support"
                className="inline-flex items-center gap-2 border border-emerald-500/20 bg-emerald-500/5 hover:border-amber-500 hover:text-amber-500 text-emerald-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
              >
                {t("dashboard.open_live_support")} <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <SupportChatPanel productId="max-os-cloud" compact />
        </section>
        </section>

        {/* ===== SECTION 1: Plan / Pricing Summary ===== */}
        <section>
          <SectionLabel>{t("dashboard.plan_pricing")}</SectionLabel>
          <DashboardCard className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <Cloud className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-display text-xl uppercase">{currentPlan}</h3>
                  <p className="text-zinc-400 text-sm mt-1">{t("dashboard.fully_managed")}</p>
                  <div className="flex items-baseline gap-2 mt-3">
                    <span className="font-display text-3xl text-white">{currentPlanPrice}</span>
                    <span className="font-mono text-xs text-zinc-500 uppercase">{t("dashboard.per month", { period: currentPlanBilling })}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors">
                  <CreditCard className="w-3.5 h-3.5" /> {t("dashboard.buy_more_credits")}
                </button>
                <button className="flex items-center gap-2 border border-zinc-700 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors">
                  <Plus className="w-3.5 h-3.5" /> {t("dashboard.add_agent")}
                </button>
              </div>
            </div>
          </DashboardCard>
        </section>

        {/* ===== SECTION 2: Credits & Usage ===== */}
        <section data-tour="credits-section">
          <SectionLabel>{t("dashboard.credits_usage")}</SectionLabel>
          <DashboardCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg uppercase">{t("dashboard.credits_used")}</h3>
                <p className="text-zinc-500 text-sm mt-1">
                  {t("dashboard.credits_consumed", { used: formatCredits(creditUsed), quota: formatCredits(creditQuota) })}
                </p>
              </div>
              <div className="text-right">
                <div className={`font-display text-2xl ${creditPct >= 90 ? "text-red-400" : creditPct >= 75 ? "text-amber-400" : "text-emerald-400"}`}>
                  {creditPct}%
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("dashboard.used")}</div>
              </div>
            </div>
            <UsageBar pct={creditPct} />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="font-mono text-[10px] text-zinc-500">0-75%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="font-mono text-[10px] text-zinc-500">75-90%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="font-mono text-[10px] text-zinc-500">90%+</span>
                </div>
              </div>
              {creditPct >= 75 && (
                <button className="flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors">
                  <TrendingUp className="w-3.5 h-3.5" /> {t("dashboard.upgrade_credits")}
                </button>
              )}
            </div>
          </DashboardCard>
        </section>

        {/* ===== SECTION 2b: Referral Program ===== */}
        <section>
          <ReferralWidget />
        </section>

        {/* ===== SECTION 2c: Rolling 5-Hour Window ===== */}
        <section>
          <SectionLabel>{t("dashboard.rolling_window")}</SectionLabel>
          <DashboardCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg uppercase">{t("dashboard.5h_usage")}</h3>
                <p className="text-zinc-500 text-sm mt-1">
                  {t("dashboard.rolling_desc")}
                </p>
              </div>
              <div className="text-right">
                <div className={`font-display text-2xl ${rolling5hPct >= 90 ? "text-red-400" : rolling5hPct >= 75 ? "text-amber-400" : "text-emerald-400"}`}>
                  {rolling5hPct}%
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("dashboard.resets_every")}</div>
              </div>
            </div>
            <UsageBar pct={rolling5hPct} />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-zinc-500" />
                <span className="font-mono text-[10px] text-zinc-500">
                  {t("dashboard.credits_used_window", { used: formatCredits(rolling5hUsed), quota: formatCredits(rolling5hQuota) })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-sky-500" />
                <span className="font-mono text-[10px] text-zinc-500">{t("dashboard.credits_remaining", { remaining: formatCredits(rolling5hQuota - rolling5hUsed) })}</span>
              </div>
            </div>
          </DashboardCard>
        </section>

        {/* ===== SECTION 3-5: Agents, Storage, Integrations in a grid ===== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Active Agents */}
          <section data-tour="agents-section">
            <SectionLabel>{t("dashboard.active_agents")}</SectionLabel>
            <DashboardCard className="p-5 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-sm uppercase">{t("dashboard.agents")}</h3>
                <span className="font-mono text-[10px] text-zinc-500">{agentCount} {t("dashboard.active").toLowerCase()}</span>
              </div>
              <div className="space-y-3">
                {agents.length > 0 ? agents.map((a) => (
                  <div key={a.agent_id} className="flex items-center gap-3 p-3 border border-zinc-800 bg-zinc-900/30">
                    <div className="w-8 h-8 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-sm uppercase truncate">{a.name}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{a.role}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="font-mono text-[10px] text-emerald-500 uppercase">{a.status}</span>
                    </div>
                  </div>
                )) : (
                  <div className="flex items-center gap-3 p-3 border border-zinc-800 bg-zinc-900/30">
                    <div className="w-8 h-8 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-sm uppercase">{t('dashboard.primary_agent')}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{t("dashboard.agent_general_ops")}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="font-mono text-[10px] text-emerald-500 uppercase">{t("dashboard.active")}</span>
                    </div>
                  </div>
                )}
              </div>
              <button className="w-full mt-4 flex items-center justify-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors">
                <Plus className="w-3.5 h-3.5" /> {t("dashboard.add_another_agent")}
              </button>
            </DashboardCard>
          </section>

          {/* Storage Usage */}
          <section data-tour="storage-section">
            <SectionLabel>{t("dashboard.storage")}</SectionLabel>
            <DashboardCard className="p-5 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-sm uppercase">{t("dashboard.storage")}</h3>
                <HardDrive className="w-4 h-4 text-zinc-500" />
              </div>
              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-2xl">{storageUsed}</span>
                  <span className="font-mono text-xs text-zinc-500">/ {storageQuota} GB</span>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">{storagePct}% {t("dashboard.used")}</div>
              </div>
              <div className="h-2 bg-zinc-900 w-full mb-4">
                <div
                  className={`h-full transition-all duration-500 ${storagePct >= 90 ? "bg-red-500" : storagePct >= 75 ? "bg-amber-400" : "bg-sky-500"}`}
                  style={{ width: `${Math.min(storagePct, 100)}%` }}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between font-mono text-[10px] text-zinc-500">
                  <span>{t("dashboard.agent_data")}</span><span>0.8 GB</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[10px] text-zinc-500">
                  <span>{t("dashboard.memory_files")}</span><span>1.2 GB</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[10px] text-zinc-500">
                  <span>{t("dashboard.logs_cache")}</span><span>0.4 GB</span>
                </div>
              </div>
            </DashboardCard>
          </section>

          {/* Connected Integrations - using the new widget */}
          <section data-tour="integrations-section">
            <SectionLabel>{t("dashboard.integrations")}</SectionLabel>
            <ConnectedIntegrationsWidget />
          </section>
        </div>

        {/* ===== SECTION 6: Recent Audit Events ===== */}
        <section>
          <SectionLabel>{t("dashboard.recent_activity")}</SectionLabel>
          <DashboardCard>
            <div className="divide-y divide-zinc-900">
              {auditEvents.length > 0 ? auditEvents.map((evt) => (
                <div key={evt.event_id} className="flex items-start gap-4 px-5 py-4 hover:bg-zinc-950/50 transition-colors">
                  <div className="mt-0.5 shrink-0">
                    {auditIcon(evt.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-zinc-300">{evt.detail}</div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-1 uppercase">{evt.action}</div>
                  </div>
                  <div className="font-mono text-[10px] text-zinc-600 shrink-0">
                    {new Date(evt.created_at).toLocaleDateString()}
                  </div>
                </div>
              )) : (
                <div className="px-5 py-8 text-center text-zinc-600 font-mono text-xs">{t("dashboard.no_recent_activity")}</div>
              )}
            </div>
          </DashboardCard>
        </section>

        {/* ===== SECTION 6b: Analytics Dashboard ===== */}
        <section data-tour="analytics-section">
          <div className="flex items-center justify-between mb-4">
            <SectionLabel>{t("dashboard.analytics_section")}</SectionLabel>
            <Link
              to="/dashboard/analytics"
              className="inline-flex items-center gap-2 border border-amber-500/20 bg-amber-500/5 hover:border-amber-500 hover:text-amber-500 text-amber-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("dashboard.full_dashboard")} <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <DashboardCard className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { l: t("dashboard.messages_today"), v: "1,247", trend: "+12.5%", up: true },
                { l: t("dashboard.avg_response"), v: "1.2s", trend: "-0.3s", up: true },
                { l: t("dashboard.auto_resolution"), v: "78.5%", trend: "+3.2%", up: true },
                { l: t("dashboard.nps_score"), v: "72", trend: "+5pts", up: true },
              ].map((s) => (
                <div key={s.l} className="border border-zinc-800 bg-zinc-950/50 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{s.l}</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-xl text-white">{s.v}</span>
                    <span className={`font-mono text-[10px] ${s.up ? "text-emerald-400" : "text-red-400"}`}>{s.trend}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-zinc-800 bg-zinc-950/50 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("dashboard.7day_volume")}</span>
              </div>
              <div className="flex items-end gap-1 h-24">
                {messagesLast7Days.map((d, i) => {
                  const max = Math.max(...messagesLast7Days.map(x => x.messages));
                  const h = (d.messages / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-amber-500/80 hover:bg-amber-500 transition-colors rounded-t-sm min-h-[4px]"
                        style={{ height: `${h}%` }}
                        title={`${d.day}: ${d.messages} messages`}
                      />
                      <span className="font-mono text-[8px] text-zinc-600">{d.day}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </DashboardCard>
        </section>

        {/* ===== A/B TESTING WIDGET ===== */}
        <section data-tour="ab-testing-section">
          <div className="flex items-center justify-between mb-4">
            <SectionLabel>{t("dashboard.ab_testing")}</SectionLabel>
            <Link
              to="/dashboard/ab-testing"
              className="inline-flex items-center gap-2 border border-amber-500/20 bg-amber-500/5 hover:border-amber-500 hover:text-amber-500 text-amber-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("dashboard.manage_tests")} <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <DashboardCard className="p-6">
            {abTests.length > 0 ? (
              <>
                {(() => {
                  const activeTests = abTests.filter(t => t.status === 'running');
                  const latestTest = activeTests.length > 0 ? activeTests[0] : abTests[0];
                  const tc = latestTest.status === 'running' ? { dot: 'bg-emerald-500', text: 'text-emerald-400' }
                    : latestTest.status === 'completed' ? { dot: 'bg-sky-500', text: 'text-sky-400' }
                    : { dot: 'bg-amber-500', text: 'text-amber-400' };
                  return (
                    <div className="mb-5 p-4 border border-zinc-800 bg-zinc-900/30">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FlaskConical className="w-4 h-4 text-amber-500" />
                          <span className="font-display text-sm uppercase">{latestTest.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${tc.dot} ${latestTest.status === 'running' ? 'animate-pulse' : ''}`} />
                          <span className={`font-mono text-[9px] uppercase tracking-wider ${tc.text}`}>{latestTest.status}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">{t('dashboard.var_a')}</div>
                          <div className="font-mono text-xs text-amber-400 mt-0.5">{latestTest.stats.rateA}%</div>
                        </div>
                        <div>
                          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">{t('dashboard.var_b')}</div>
                          <div className="font-mono text-xs text-sky-400 mt-0.5">{latestTest.stats.rateB}%</div>
                        </div>
                        <div>
                          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">{t('dashboard.lift')}</div>
                          <div className={`font-mono text-xs mt-0.5 ${latestTest.stats.lift > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {latestTest.stats.lift > 0 ? '+' : ''}{latestTest.stats.lift}%
                          </div>
                        </div>
                        <div>
                          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">{t('dashboard.conf')}</div>
                          <div className="font-mono text-xs text-zinc-300 mt-0.5">{latestTest.stats.confidence}%</div>
                        </div>
                      </div>
                      {latestTest.stats.winner && (
                        <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 text-emerald-400">
                          <Target className="w-3.5 h-3.5" />
                          <span className="font-mono text-[10px] uppercase tracking-wider">
                            {latestTest.stats.winner === 'b' ? t("dashboard.variant_b_winning") : t("dashboard.variant_a_winning")}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="font-mono text-[10px] text-zinc-500">{abTests.filter(t => t.status === 'running').length} {t("dashboard.running")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-sky-500" />
                      <span className="font-mono text-[10px] text-zinc-500">{abTests.filter(t => t.status === 'completed').length} {t("dashboard.completed")}</span>
                    </div>
                  </div>
                  <Link
                    to="/dashboard/ab-testing"
                    className="font-mono text-[10px] text-amber-500 hover:text-amber-400 uppercase tracking-wider transition-colors"
                  >
                    {t("dashboard.view_all")}
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <FlaskConical className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">{t("dashboard.no_ab_tests")}</p>
                <Link
                  to="/dashboard/ab-testing"
                  className="inline-flex items-center gap-1.5 mt-2 text-amber-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-wider transition-colors"
                >
                  {t("dashboard.create_first_test")} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}
          </DashboardCard>
        </section>

        {/* ===== COMMUNITY WIDGET ===== */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <SectionLabel>{t("dashboard.community_section")}</SectionLabel>
            <Link
              to="/community"
              className="inline-flex items-center gap-2 border border-amber-500/20 bg-amber-500/5 hover:border-amber-500 hover:text-amber-500 text-amber-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("dashboard.view_all_posts")} <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <DashboardCard className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-5 h-5 text-amber-500" />
              <h3 className="font-display text-lg uppercase">{t("dashboard.recent_discussions")}</h3>
            </div>
            <div className="divide-y divide-zinc-800">
              {[
                { title: "How to create my first AI agent?", replies: 8, url: "/community/topic/gs-01" },
                { title: "Automating social media posts across platforms", replies: 15, url: "/community/topic/tt-01" },
                { title: "AI-powered summary for long discussions", replies: 14, url: "/community/topic/fr-01" },
              ].map((topic) => (
                <Link
                  key={topic.title}
                  to={topic.url}
                  className="flex items-center justify-between py-3 hover:bg-zinc-900/50 -mx-6 px-6 transition-colors"
                >
                  <span className="text-sm text-zinc-300 hover:text-amber-400 transition-colors flex-1 min-w-0 truncate mr-4">
                    {topic.title}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                    {topic.replies} {t("dashboard.replies")}
                  </span>
                </Link>
              ))}
            </div>
            <Link
              to="/community/new"
              className="mt-4 flex items-center justify-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" /> {t("dashboard.start_discussion")}
            </Link>
          </DashboardCard>
        </section>

        {/* ===== AUTOMATIONS WIDGET ===== */}
        <section data-tour="automations-section">
          <div className="flex items-center justify-between mb-4">
            <SectionLabel>{t("dashboard.automations")}</SectionLabel>
            <Link
              to="/dashboard/automations"
              className="inline-flex items-center gap-2 border border-amber-500/20 bg-amber-500/5 hover:border-amber-500 hover:text-amber-500 text-amber-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("dashboard.open_builder")} <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <DashboardCard className="p-6">
            {automations.length > 0 ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Workflow className="w-5 h-5 text-amber-500" />
                  <h3 className="font-display text-lg uppercase">{t("dashboard.active_automations")}</h3>
                </div>
                <div className="divide-y divide-zinc-800">
                  {automations.slice(0, 4).map((auto) => (
                    <Link
                      key={auto.id}
                      to="/dashboard/automations"
                      className="flex items-center justify-between py-3 hover:bg-zinc-900/50 -mx-6 px-6 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${auto.status === 'active' ? 'bg-emerald-500' : auto.status === 'paused' ? 'bg-amber-500' : 'bg-zinc-500'}`} />
                        <span className="text-sm text-zinc-300 hover:text-amber-400 transition-colors truncate">
                          {auto.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono text-[10px] text-zinc-500">
                          {auto.nodes?.length || 0} {t("dashboard.nodes")}
                        </span>
                        <span className={`font-mono text-[9px] uppercase tracking-wider ${auto.status === 'active' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {auto.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="font-mono text-[10px] text-zinc-500">{automations.filter(a => a.status === 'active').length} active</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="font-mono text-[10px] text-zinc-500">{automations.filter(a => a.status === 'paused').length} paused</span>
                    </div>
                  </div>
                  <Link
                    to="/dashboard/automations"
                    className="font-mono text-[10px] text-amber-500 hover:text-amber-400 uppercase tracking-wider transition-colors"
                  >
                    {t("dashboard.view_all")}
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <Workflow className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">{t("dashboard.no_automations")}</p>
                <Link
                  to="/dashboard/automations"
                  className="inline-flex items-center gap-1.5 mt-2 text-amber-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-wider transition-colors"
                >
                  {t("dashboard.create_first_automation")} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}
          </DashboardCard>
        </section>

        {/* ===== SECTION 7: Upgrade / Purchase CTAs ===== */}
        {creditPct >= 75 && (
          <section>
            <SectionLabel>{t("dashboard.recommended")}</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DashboardCard className="p-6 border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg uppercase">{t("dashboard.need_more_credits")}</h3>
                    <p className="text-zinc-400 text-sm mt-1">{t("dashboard.add_credit_desc")}</p>
                    <button className="mt-4 flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors">
                      <CreditCard className="w-3.5 h-3.5" /> {t("dashboard.buy_more_credits")}
                    </button>
                  </div>
                </div>
              </DashboardCard>
              <DashboardCard className="p-6 border-sky-500/30 bg-gradient-to-br from-sky-500/5 to-transparent">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-sky-500/10 border border-sky-500/30 flex items-center justify-center shrink-0">
                    <Bot className="w-5 h-5 text-sky-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg uppercase">{t("dashboard.scale_team")}</h3>
                    <p className="text-zinc-400 text-sm mt-1">{t("dashboard.add_agent_desc")}</p>
                    <button className="mt-4 flex items-center gap-2 border border-sky-500/50 hover:border-sky-400 text-sky-400 hover:text-sky-300 px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors">
                      <Plus className="w-3.5 h-3.5" /> {t("dashboard.add_one_more_agent")}
                    </button>
                  </div>
                </div>
              </DashboardCard>
            </div>
          </section>
        )}

        {/* Future marketplace placeholder */}
        <section>
          <SectionLabel>{t("dashboard.agent_marketplace")}</SectionLabel>
          <DashboardCard className="p-8 text-center border-dashed border-zinc-700">
            <Sparkles className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <h3 className="font-display text-lg uppercase text-zinc-500">{t("dashboard.coming_soon")}</h3>
            <p className="text-zinc-600 text-sm mt-2 max-w-md mx-auto">
              {t("dashboard.marketplace_desc")}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              {[t("dashboard.social_agent"), t("dashboard.support_agent"), t("dashboard.whatsapp_agent"), t("dashboard.skills_addons")].map((name) => (
                <span key={name} className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 border border-zinc-800 px-3 py-1.5">
                  {name}
                </span>
              ))}
            </div>
          </DashboardCard>
        </section>

        {/* ===== Existing: Course CTA if access granted ===== */}
        {courseAccess && (
          <section data-tour="course-section">
            <SectionLabel>{t("dashboard.learning")}</SectionLabel>
            <Link
              to="/dashboard/course"
              data-testid="course-cta"
              className="group block relative overflow-hidden border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-6 hover:border-amber-500 transition-colors"
            >
              <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
              <div className="relative flex items-center gap-5">
                <div className="w-12 h-12 bg-amber-500 text-black flex items-center justify-center shrink-0">
                  <GraduationCap className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase">{t("dashboard.continue_course")}</div>
                  <div className="text-zinc-400 text-sm mt-0.5">{t("dashboard.course_desc")}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </section>
        )}

        {/* ===== Existing: Subscriptions ===== */}
        {subs.length > 0 && (
          <section>
            <SectionLabel>{t("dashboard.recurring")}</SectionLabel>
            <h2 className="font-display text-xl uppercase mb-4">{t("dashboard.your_subscriptions")}</h2>
            <DashboardCard>
              <div className="divide-y divide-zinc-900">
                {subs.map((s) => (
                  <div key={s.session_id} data-testid={`sub-row-${s.product_id}`} className="flex items-center justify-between p-5">
                    <div>
                      <div className="font-display text-lg uppercase">{s.product?.name || s.product_id}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                        {s.cancel_at_period_end ? t("dashboard.cancels_on") : t("dashboard.renews_on")}{" "}
                        {new Date(s.current_period_end).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 ${
                        s.cancel_at_period_end ? "text-red-400 border border-red-900" :
                        s.status === "active" ? "text-green-400 border border-green-900" :
                        "text-zinc-400 border border-zinc-800"
                      }`}>
                        {s.cancel_at_period_end ? t("dashboard.canceling") : s.status?.toUpperCase() || "UNKNOWN"}
                      </span>
                      {!s.cancel_at_period_end && s.status === "active" && (
                        <button
                          data-testid={`cancel-sub-${s.product_id}`}
                          onClick={() => handleCancelSub(s.session_id)}
                          className="flex items-center gap-2 border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-400 px-3 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" /> {t("dashboard.cancel")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>
          </section>
        )}

        {/* ===== Existing: Orders / Library ===== */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <SectionLabel>// LIBRARY</SectionLabel>
            <Link
              to="/pricing"
              data-testid="dashboard-shop-link"
              className="hidden sm:inline-flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("dashboard.browse_catalog")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <h2 className="font-display text-xl uppercase mb-4">{t("dashboard.your_purchases")}</h2>

          {loading ? (
            <div className="text-zinc-500 font-mono text-sm">{t("common.loading")}</div>
          ) : orders.length === 0 ? (
            <div data-testid="empty-orders" className="border border-zinc-900 p-12 text-center">
              <Package className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
              <div className="font-display text-xl uppercase mb-2">{t("dashboard.no_purchases")}</div>
              <p className="text-zinc-500 max-w-md mx-auto mb-6 text-sm">
                {t("dashboard.empty_library")}
              </p>
              <Link
                to="/pricing"
                data-testid="empty-shop-cta"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors"
              >
                {t("dashboard.browse_catalog")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <DashboardCard>
              <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-zinc-950 border-b border-zinc-900 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <div className="col-span-5">{t('dashboard.product')}</div>
                <div className="col-span-3 hidden md:block">{t('dashboard.order')}</div>
                <div className="col-span-2">{t('dashboard.amount')}</div>
                <div className="col-span-2 text-right">{t('dashboard.action')}</div>
              </div>
              <div className="divide-y divide-zinc-900">
                {orders.map((o) => (
                  <div
                    key={o.order_id}
                    data-testid={`order-row-${o.product_id}`}
                    className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-zinc-950/50"
                  >
                    <div className="col-span-5">
                      <div className="font-display text-base uppercase">{o.product?.name || o.product_id}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                        {new Date(o.created_at).toLocaleDateString()} · {o.mode === "subscription" ? "SUBSCRIPTION" : "ONE-TIME"}
                      </div>
                    </div>
                    <div className="col-span-3 hidden md:block font-mono text-xs text-zinc-400 truncate">
                      {o.order_id}
                    </div>
                    <div className="col-span-2 font-display text-base">${Number(o.amount).toFixed(0)}</div>
                    <div className="col-span-2 flex justify-end gap-2">
                      {o.product?.grants_course_access && (
                        <Link
                          to="/dashboard/course"
                          data-testid={`course-${o.product_id}-button`}
                          className="hidden md:inline-flex items-center gap-1.5 border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black px-3 py-1.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
                        >
                          <GraduationCap className="w-3 h-3" /> Course
                        </Link>
                      )}
                      {o.product?.download_filename && (
                        <button
                          data-testid={`download-${o.product_id}-button`}
                          onClick={() => handleDownload(o.product_id)}
                          className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
                        >
                          <Download className="w-3 h-3" /> Download
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>
          )}
        </section>
      </div>

      {showPreviewForInactive && (
        <div className="border-t border-zinc-900 mt-16 pt-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <MaxOSCloudPreview embedded />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
